import { useState, useEffect } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { ShoppingCart, Plus, Minus, X, ShoppingBag, Check, Leaf } from 'lucide-react';
import { toast } from 'sonner';
import { useCart } from '../context/CartContext';
import { useSiteSettings } from '../hooks/useSiteSettings';
import { SeoHead } from '../components/SeoHead';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND_URL}/api`;

const CAT_LABELS = {
  all: 'Tutti',
  archivio: 'Archivio',
  freschi: 'Prodotti Freschi',
  specialita: 'Specialità Locali',
  conserve: 'Conserve',
};

// Label abbreviate per mobile (una riga)
const CAT_LABELS_SHORT = {
  all: 'Tutti',
  archivio: 'Archivio',
  freschi: 'Freschi',
  specialita: 'Specialità',
  conserve: 'Conserve',
};

function formatPrice(price) {
  if (!price || price === 0) return null;
  return `€ ${Number(price).toFixed(2).replace('.', ',')}`;
}

/* ─── Checkout Modal ─────────────────────────────────────────────────────── */
function CheckoutModal({ open, onClose, onSuccess, whatsappNumber }) {
  const { items, total, clearCart } = useCart();
  const [form, setForm] = useState({
    customer_name: '', customer_phone: '', customer_email: '',
    customer_address: '', notes: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [waUrl, setWaUrl] = useState('');

  if (!open) return null;

  const buildWhatsAppUrl = (formData, orderItems, orderTotal) => {
    const phone = (whatsappNumber || '393382523925').replace(/[^0-9]/g, '');
    const lines = [
      '🛒 *NUOVO PREORDINE — Le Bontà del Vesuvio*',
      '',
      `👤 *Nome:* ${formData.customer_name}`,
      `📞 *Telefono:* ${formData.customer_phone}`,
      formData.customer_email ? `📧 *Email:* ${formData.customer_email}` : null,
      formData.customer_address ? `📍 *Indirizzo:* ${formData.customer_address}` : null,
      '',
      '🛍️ *Articoli ordinati:*',
      ...orderItems.map(i =>
        `• ${i.product_name} × ${i.quantity}${i.price > 0 ? ` — ${formatPrice(i.price * i.quantity)}` : ''}`
      ),
      orderTotal > 0 ? `\n💰 *Totale stimato: ${formatPrice(orderTotal)}*` : null,
      formData.notes ? `\n📝 *Note:* ${formData.notes}` : null,
      '',
      'Ho effettuato un preordine sul vostro sito. Potete confermare la disponibilità?',
    ].filter(l => l !== null).join('\n');
    return `https://wa.me/${phone}?text=${encodeURIComponent(lines)}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.customer_name.trim() || !form.customer_phone.trim()) {
      toast.error('Nome e telefono sono obbligatori');
      return;
    }
    setSubmitting(true);
    try {
      const orderItems = items.map(({ product, quantity }) => ({
        product_id: product.id,
        product_name: product.name,
        product_image: product.image_url,
        price: product.price || 0,
        ean: product.ean || '',
        codart: product.codart || '',
        name_mdd: product.name_mdd || '',
        prezzo_mdd: product.prezzo_mdd || 0,
        prezzo_golosi: product.prezzo_golosi || product.price || 0,
        quantity,
      }));
      await axios.post(`${API}/orders`, { ...form, items: orderItems, total });
      setWaUrl(buildWhatsAppUrl(form, orderItems, total));
      setSuccess(true);
      clearCart();
    } catch {
      toast.error("Errore nell'invio del preordine. Riprova.");
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <div className="fixed inset-0 bg-black/70 z-[60] flex items-center justify-center p-4">
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className="bg-white rounded-2xl p-8 w-full max-w-md text-center"
        >
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Check className="w-8 h-8 text-green-600" />
          </div>
          <h3 className="font-heading text-2xl font-bold text-gray-900 mb-2">Preordine salvato!</h3>
          <p className="font-body text-gray-500 mb-6">
            Il tuo preordine è stato registrato. Ora inviaci un messaggio su WhatsApp per confermarlo — ti risponderemo al più presto!
          </p>

          {/* WhatsApp CTA — primary action */}
          <a
            href={waUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-center gap-3 w-full bg-[#25D366] hover:bg-[#20BD5A] text-white font-body font-bold py-4 rounded-xl transition-colors text-base mb-3"
          >
            <svg viewBox="0 0 24 24" className="w-6 h-6 fill-current" xmlns="http://www.w3.org/2000/svg">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
            </svg>
            Conferma su WhatsApp
          </a>

          <button
            onClick={onSuccess}
            className="w-full border border-gray-200 text-gray-600 font-body font-medium py-3 rounded-xl hover:bg-gray-50 transition-colors text-sm"
          >
            Torna al negozio
          </button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/70 z-[60] flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl w-full max-w-lg max-h-[92vh] overflow-y-auto shadow-2xl">
        <div className="flex items-center justify-between p-6 border-b">
          <h3 className="font-heading text-xl font-bold">Completa il preordine</h3>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Order Summary */}
        <div className="p-5 bg-gray-50 border-b">
          <p className="font-body text-sm font-semibold text-gray-700 mb-3">
            Riepilogo — {items.length} {items.length === 1 ? 'prodotto' : 'prodotti'}
          </p>
          <div className="space-y-1.5">
            {items.map(({ product, quantity }) => (
              <div key={product.id} className="flex justify-between font-body text-sm text-gray-600">
                <span>{product.name} × {quantity}</span>
                <span>{product.price > 0 ? formatPrice(product.price * quantity) || '—' : '—'}</span>
              </div>
            ))}
          </div>
          {total > 0 && (
            <div className="flex justify-between font-body font-semibold text-gray-900 mt-3 pt-3 border-t border-gray-200">
              <span>Totale stimato</span>
              <span className="text-primary">{formatPrice(total)}</span>
            </div>
          )}
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block font-body text-sm font-medium text-gray-700 mb-1.5">
                Nome e cognome <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={form.customer_name}
                onChange={e => setForm({ ...form, customer_name: e.target.value })}
                className="w-full border border-gray-200 rounded-xl px-3 py-2.5 font-body text-sm focus:outline-none focus:ring-2 focus:ring-primary/20"
                placeholder="Mario Rossi"
                required
              />
            </div>
            <div>
              <label className="block font-body text-sm font-medium text-gray-700 mb-1.5">
                Telefono <span className="text-red-500">*</span>
              </label>
              <input
                type="tel"
                value={form.customer_phone}
                onChange={e => setForm({ ...form, customer_phone: e.target.value })}
                className="w-full border border-gray-200 rounded-xl px-3 py-2.5 font-body text-sm focus:outline-none focus:ring-2 focus:ring-primary/20"
                placeholder="+39 333 000 0000"
                required
              />
            </div>
          </div>
          <div>
            <label className="block font-body text-sm font-medium text-gray-700 mb-1.5">Email</label>
            <input
              type="email"
              value={form.customer_email}
              onChange={e => setForm({ ...form, customer_email: e.target.value })}
              className="w-full border border-gray-200 rounded-xl px-3 py-2.5 font-body text-sm focus:outline-none focus:ring-2 focus:ring-primary/20"
              placeholder="mario@email.com"
            />
          </div>
          <div>
            <label className="block font-body text-sm font-medium text-gray-700 mb-1.5">Indirizzo di consegna</label>
            <input
              type="text"
              value={form.customer_address}
              onChange={e => setForm({ ...form, customer_address: e.target.value })}
              className="w-full border border-gray-200 rounded-xl px-3 py-2.5 font-body text-sm focus:outline-none focus:ring-2 focus:ring-primary/20"
              placeholder="Via Roma 1, Napoli..."
            />
          </div>
          <div>
            <label className="block font-body text-sm font-medium text-gray-700 mb-1.5">Note aggiuntive</label>
            <textarea
              value={form.notes}
              onChange={e => setForm({ ...form, notes: e.target.value })}
              rows={3}
              className="w-full border border-gray-200 rounded-xl px-3 py-2.5 font-body text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 resize-none"
              placeholder="Quantità, preferenze di consegna, orari..."
            />
          </div>

          <p className="font-body text-xs text-gray-400 bg-amber-50 border border-amber-100 rounded-xl px-4 py-3">
            📦 Questo è un <strong>preordine</strong>. Dopo l'invio ti verrà chiesto di <strong>confermare via WhatsApp</strong> — ti risponderemo per finalizzare disponibilità e consegna.
          </p>

          <div className="flex gap-3 pt-1">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 border border-gray-200 font-body font-medium py-3 rounded-xl hover:bg-gray-50 transition-colors"
            >
              Annulla
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="flex-1 bg-primary hover:bg-primary-dark text-white font-body font-semibold py-3 rounded-xl transition-colors disabled:opacity-60"
            >
              {submitting ? 'Salvataggio...' : 'Conferma Preordine →'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/* ─── Cart Drawer ─────────────────────────────────────────────────────────── */
export function CartDrawer({ open, onClose, whatsappNumber }) {
  const { items, removeFromCart, updateQty, total } = useCart();
  const [checkoutOpen, setCheckoutOpen] = useState(false);

  const handleCheckoutSuccess = () => {
    setCheckoutOpen(false);
    onClose();
  };

  return (
    <>
      <AnimatePresence>
        {open && (
          <>
            <motion.div
              key="backdrop"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={onClose}
              className="fixed inset-0 bg-black/50 z-40"
            />
            <motion.div
              key="drawer"
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'spring', damping: 28, stiffness: 320 }}
              className="fixed right-0 top-0 h-full w-full max-w-md bg-white z-50 shadow-2xl flex flex-col"
            >
              <div className="flex items-center justify-between p-5 border-b">
                <div className="flex items-center gap-2">
                  <ShoppingCart className="w-5 h-5 text-primary" />
                  <h3 className="font-heading text-xl font-bold">Il tuo carrello</h3>
                </div>
                <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
                  <X className="w-5 h-5" />
                </button>
              </div>

              {items.length === 0 ? (
                <div className="flex-1 flex flex-col items-center justify-center text-center gap-4 p-8">
                  <ShoppingBag className="w-16 h-16 text-gray-200" />
                  <p className="font-body text-lg text-gray-500">Il carrello è vuoto</p>
                  <p className="font-body text-sm text-gray-400">Sfoglia i prodotti e aggiungi ciò che ti piace!</p>
                  <button onClick={onClose} className="mt-2 bg-primary hover:bg-primary-dark text-white font-body font-medium px-6 py-2.5 rounded-xl transition-colors">
                    Sfoglia prodotti
                  </button>
                </div>
              ) : (
                <>
                  <div className="flex-1 overflow-y-auto p-5 space-y-4">
                    {items.map(({ product, quantity }) => (
                      <div key={product.id} className="flex gap-3 items-center bg-gray-50 rounded-xl p-3">
                        {product.image_url ? (
                          <img
                            src={product.image_url}
                            alt={product.name}
                            loading="lazy"
                            decoding="async"
                            className="w-14 h-14 object-cover rounded-lg flex-shrink-0"
                            onError={e => { e.target.style.display='none'; e.target.nextSibling.style.display='flex'; }}
                          />
                        ) : null}
                        <div
                          className="w-14 h-14 rounded-lg flex-shrink-0 bg-primary/10 flex items-center justify-center"
                          style={{ display: product.image_url ? 'none' : 'flex' }}
                        >
                          <ShoppingBag className="w-6 h-6 text-primary/40" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-body font-semibold text-gray-900 truncate text-sm">{product.name}</p>
                          {product.price > 0 && (
                            <p className="font-body text-xs text-primary font-medium">{formatPrice(product.price)} / cad.</p>
                          )}
                          <div className="flex items-center gap-2 mt-1.5">
                            <button
                              onClick={() => updateQty(product.id, quantity - 1)}
                              className="w-6 h-6 rounded-full border border-gray-200 flex items-center justify-center hover:bg-white hover:shadow-sm transition-all"
                            >
                              <Minus className="w-3 h-3" />
                            </button>
                            <span className="font-body text-sm font-semibold w-5 text-center">{quantity}</span>
                            <button
                              onClick={() => updateQty(product.id, quantity + 1)}
                              className="w-6 h-6 rounded-full border border-gray-200 flex items-center justify-center hover:bg-white hover:shadow-sm transition-all"
                            >
                              <Plus className="w-3 h-3" />
                            </button>
                          </div>
                        </div>
                        {product.price > 0 && (
                          <p className="font-body text-sm font-bold text-gray-800 flex-shrink-0">
                            {formatPrice(product.price * quantity)}
                          </p>
                        )}
                        <button
                          onClick={() => removeFromCart(product.id)}
                          className="p-1.5 hover:bg-red-50 rounded-lg text-gray-300 hover:text-red-500 transition-colors"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>

                  <div className="p-5 border-t space-y-3 bg-gray-50">
                    {total > 0 && (
                      <div className="flex justify-between items-center">
                        <span className="font-body text-gray-700 font-medium">Totale stimato</span>
                        <span className="font-heading text-xl font-bold text-primary">{formatPrice(total)}</span>
                      </div>
                    )}
                    <p className="font-body text-xs text-gray-400 text-center">
                      Il prezzo finale sarà confermato al momento dell'ordine
                    </p>
                    <button
                      onClick={() => setCheckoutOpen(true)}
                      className="w-full bg-primary hover:bg-primary-dark text-white font-body font-semibold py-3 rounded-xl transition-colors"
                    >
                      Procedi all'ordine →
                    </button>
                  </div>
                </>
              )}
            </motion.div>
          </>
        )}
      </AnimatePresence>

      <CheckoutModal
        open={checkoutOpen}
        onClose={() => setCheckoutOpen(false)}
        onSuccess={handleCheckoutSuccess}
        whatsappNumber={whatsappNumber}
      />
    </>
  );
}

/* ─── Product Card ────────────────────────────────────────────────────────── */
function ProductCard({ product, onAddToCart }) {
  const { items, updateQty, removeFromCart } = useCart();
  const unavailable = product.available === false;

  // Quantità già nel carrello per questo prodotto
  const cartItem  = items.find(i => i.product.id === product.id);
  const cartQty   = cartItem?.quantity ?? 0;
  const inCart    = cartQty > 0;

  const handleAdd = () => {
    if (unavailable) return;
    onAddToCart(product, 1);
  };

  const handleInc = (e) => {
    e.stopPropagation();
    onAddToCart(product, 1);
  };

  const handleDec = (e) => {
    e.stopPropagation();
    if (cartQty <= 1) removeFromCart(product.id);
    else updateQty(product.id, cartQty - 1);
  };

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.95 }}
      className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden flex flex-col group"
    >
      {/* ── Immagine ── */}
      <div className="relative overflow-hidden bg-gray-50 aspect-square">
        {product.image_url && (
          <div
            className="absolute inset-0 scale-110 opacity-40 pointer-events-none"
            style={{ filter: 'blur(16px)', backgroundImage: `url(${product.image_url})`, backgroundSize: 'cover', backgroundPosition: 'center' }}
          />
        )}
        {product.image_url ? (
          <img
            src={product.image_url}
            alt={product.name}
            loading="lazy"
            decoding="async"
            className="relative z-10 w-full h-full object-contain p-2 transition-transform duration-500 group-hover:scale-105"
            onError={e => { e.target.style.opacity = '0.2'; }}
          />
        ) : (
          <div className="relative z-10 w-full h-full flex items-center justify-center px-4 text-center">
            <span className="font-heading text-2xl text-primary/20">{product.name?.slice(0, 1) || 'P'}</span>
          </div>
        )}
        {product.featured && (
          <span className="absolute top-2 left-2 z-20 bg-accent text-primary-dark font-body text-[10px] font-bold px-2 py-0.5 rounded-full flex items-center gap-1">
            <Leaf className="w-2.5 h-2.5" /> Top
          </span>
        )}
        {/* Badge quantità in carrello */}
        {inCart && (
          <span className="absolute top-2 right-2 z-20 bg-primary text-white font-body text-[10px] font-bold w-5 h-5 rounded-full flex items-center justify-center shadow-md">
            {cartQty}
          </span>
        )}
        {unavailable && (
          <div className="absolute inset-0 z-30 bg-black/45 flex items-center justify-center">
            <span className="bg-white/90 text-gray-700 font-body font-semibold px-3 py-1.5 rounded-full text-xs">
              Esaurito
            </span>
          </div>
        )}
      </div>

      {/* ── Info ── */}
      <div className="p-2.5 sm:p-4 flex flex-col flex-1">
        <span className="font-body text-[10px] sm:text-xs text-secondary bg-secondary/10 px-2 py-0.5 rounded-full w-fit leading-tight">
          {CAT_LABELS[product.category] || product.category}
        </span>

        <h3 className="font-heading text-xs sm:text-base font-bold text-gray-900 mt-1.5 leading-tight line-clamp-2">
          {product.name}
        </h3>

        <p className="hidden sm:block font-body text-sm text-gray-500 mt-1 line-clamp-2 flex-1">
          {product.description}
        </p>

        {/* Prezzo */}
        <div className="mt-2 pt-2 border-t border-gray-100">
          {product.price > 0 ? (
            <span className="font-heading text-sm sm:text-lg font-bold text-primary">
              {formatPrice(product.price)}
            </span>
          ) : (
            <span className="font-body text-[10px] sm:text-xs text-gray-400 italic">
              Prezzo su richiesta
            </span>
          )}
        </div>

        {/* ── Azioni: stesso layout mobile e desktop ── */}
        <div className="mt-2 sm:mt-3">
          {inCart ? (
            /* Controlli quantità — visibili quando già nel carrello */
            <div className="flex items-center gap-1.5">
              <button
                onClick={handleDec}
                className="w-8 h-8 sm:w-9 sm:h-9 rounded-xl bg-gray-100 hover:bg-red-50 hover:text-red-500 flex items-center justify-center transition-colors flex-shrink-0"
              >
                <Minus className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
              </button>

              <div className="flex-1 flex items-center justify-center gap-1 bg-primary/5 rounded-xl py-1.5 sm:py-2">
                <ShoppingCart className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-primary" />
                <span className="font-body font-bold text-xs sm:text-sm text-primary">{cartQty}</span>
              </div>

              <button
                onClick={handleInc}
                disabled={unavailable}
                className="w-8 h-8 sm:w-9 sm:h-9 rounded-xl bg-primary hover:bg-primary-dark text-white flex items-center justify-center transition-colors flex-shrink-0 disabled:opacity-40"
              >
                <Plus className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
              </button>
            </div>
          ) : (
            /* Pulsante aggiungi — quando non ancora nel carrello */
            <button
              onClick={handleAdd}
              disabled={unavailable}
              className="w-full flex items-center justify-center gap-1.5 font-body text-xs sm:text-sm font-semibold py-2 sm:py-2.5 rounded-xl bg-primary hover:bg-primary-dark text-white transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <ShoppingCart className="w-3.5 h-3.5" />
              Aggiungi
            </button>
          )}
        </div>
      </div>
    </motion.div>
  );
}

/* ─── Main Page ───────────────────────────────────────────────────────────── */
export function NegozioPage() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState('all');
  const { addToCart, count, openCart } = useCart();
  const { settings } = useSiteSettings();

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const res = await axios.get(`${API}/products`);
        setProducts(res.data);
      } catch {
        toast.error('Errore nel caricamento prodotti');
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, []);

  const categories = ['all', ...[...new Set(products.map(p => p.category))]];
  const filtered = activeCategory === 'all' ? products : products.filter(p => p.category === activeCategory);

  const handleAddToCart = (product, qty = 1) => {
    addToCart(product, qty);
    toast.success(`${product.name} aggiunto!`, {
      action: { label: 'Vedi carrello', onClick: () => openCart() },
    });
  };

  return (
    <>
      <SeoHead
        title="Negozio Online | Acquista Pomodorini del Vesuvio e Prodotti Campani"
        description="Acquista online i prodotti del Vesuvio: pomodorini napoletani freschi, conserve artigianali campane e specialità vesuviane. Azienda agricola a Ercolano (NA). Preordina e conferma via WhatsApp."
        keywords="acquista pomodorino napoletano online, prodotti del vesuvio shop, conserve vesuviane online, negozio prodotti campani, pomodoro vesuvio prezzo, specialità campane ordine online"
        canonical="/negozio"
      />

      {/* Header compatto — occupa poco, prodotti subito visibili */}
      <section className="relative pt-24 pb-5 md:pt-28 md:pb-6 text-white overflow-hidden">
        <div className="absolute inset-0 z-0">
          {settings.negozio_hero_image_url ? (
            <img src={settings.negozio_hero_image_url} alt="Negozio prodotti del Vesuvio" className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full bg-gradient-to-b from-primary-dark to-primary" />
          )}
          <div className="absolute inset-0 bg-black/60" />
        </div>
        <div className="container-custom relative z-10">
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
            <span className="font-body text-accent text-xs tracking-widest uppercase">
              Pomodorini Napoletani e Specialità Campane — Direttamente dalla nostra terra
            </span>
            <h1 className="font-heading text-2xl md:text-3xl font-bold mt-1 drop-shadow-lg">
              Negozio Online — Prodotti del Vesuvio
            </h1>
            <p className="font-body text-white/80 text-sm mt-1 max-w-lg">
              Pomodorini napoletani, conserve artigianali vesuviane e specialità campane —
              aggiungi al carrello e preordina direttamente dall'azienda agricola
            </p>
          </motion.div>
        </div>
      </section>

      <section className="py-4 md:py-8 bg-background-DEFAULT">
        <div className="container-custom">
          {/* Category Filters — una riga, no wrap, no scroll */}
          <div className="flex gap-1.5 mb-4 justify-between sm:justify-center sm:gap-2">
            {categories.map(cat => (
              <button
                key={cat}
                onClick={() => setActiveCategory(cat)}
                className={`flex-1 sm:flex-none px-2 sm:px-4 py-1.5 rounded-full font-body text-xs sm:text-sm font-medium transition-all duration-200 whitespace-nowrap text-center ${
                  activeCategory === cat
                    ? 'bg-primary text-white shadow-md'
                    : 'bg-white text-text-DEFAULT hover:bg-primary/10 border border-gray-200'
                }`}
              >
                <span className="sm:hidden">{CAT_LABELS_SHORT[cat] || cat}</span>
                <span className="hidden sm:inline">{CAT_LABELS[cat] || cat}</span>
              </button>
            ))}
          </div>

          {/* Products Grid */}
          {loading ? (
            <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-5">
              {[...Array(8)].map((_, i) => (
                <div key={i} className="bg-white rounded-2xl h-56 animate-pulse border border-gray-100" />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-5">
              <AnimatePresence>
                {filtered.map(product => (
                  <ProductCard key={product.id} product={product} onAddToCart={handleAddToCart} />
                ))}
              </AnimatePresence>
            </div>
          )}

          {!loading && filtered.length === 0 && (
            <div className="text-center py-20 text-gray-400">
              <ShoppingBag className="w-16 h-16 mx-auto mb-4 text-gray-200" />
              <p className="font-body text-lg">Nessun prodotto disponibile in questa categoria</p>
            </div>
          )}
        </div>
      </section>

      {/* Floating Cart Button */}
      <AnimatePresence>
        {count > 0 && (
          <motion.button
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0, opacity: 0 }}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => openCart()}
            className="fixed bottom-8 right-8 bg-primary hover:bg-primary-dark text-white rounded-full px-6 py-4 shadow-2xl flex items-center gap-3 z-30 font-body font-semibold"
          >
            <ShoppingCart className="w-5 h-5" />
            <span>{count} {count === 1 ? 'prodotto' : 'prodotti'}</span>
            {/* pulse ring */}
            <span className="absolute -top-1 -right-1 w-4 h-4 bg-accent rounded-full border-2 border-white" />
          </motion.button>
        )}
      </AnimatePresence>
    </>
  );
}
